<script>
import { required, minLength } from 'vuelidate/lib/validators';
import Repositories from '@/repositories';
const SuppliersRepository = Repositories.get('suppliers');
import Loading from 'vue-loading-overlay';
import 'vue-loading-overlay/dist/vue-loading.css';
import HeaderContentLayout from '@/router/layouts/header-content-layout';
import Tooltip from '@/components/widgets/errors/validations-tooltip.vue';
import ChangeValueModal from '@/components/widgets/modals/input-value';

/**
 * Dashboard Component
 */
export default {
  page: {
    title: 'Suppliers',
  },
  components: {
    Loading,
    HeaderContentLayout,
    Tooltip,
    ChangeValueModal,
  },
  data() {
    return {
      state: this.$route.name == 'editSupplier' ? 'edit' : 'create',
      isLoading: false,
      items: [
        {
          text: 'supplier',
          href: '/configuration/suppliers/create',
        },
        {
          text: '',
          active: true,
        },
      ],
      emailList: [],
      formObject: {
        name: '',
        contact: '',
        phone: '',
        link: '',
        method: 'link',
        language: this.$i18n.locale,
        address: '',
        paymentMethod: '',
        paymentCondition: '',
      },
    };
  },
  validations: {
    formObject: {
      name: { required, minLength: minLength(3) },
    },
  },
  created() {
    this.loadData();
  },
  methods: {
    loadData() {
      this.isLoading = true;
      if (this.$route.params.idPublic != undefined) {
        SuppliersRepository.get(this.$route.params.idPublic)
          .then((response) => {
            this.formObject.name = response.data.name;
            this.formObject.contact = response.data.contact;
            this.formObject.phone = response.data.phone;
            this.formObject.link = response.data.link;
            this.formObject.method = response.data.method;
            this.formObject.language = response.data.language;
            this.formObject.address = response.data.address;
            this.formObject.paymentMethod = response.data.paymentMethod;
            this.formObject.paymentCondition = response.data.paymentCondition;

            if (response.data.email != null && response.data.email.length > 0) {
              this.emailList = response.data.email.split(';');
            }

            this.isLoading = false;
          })
          .catch(() => {
            this.$toast.errorToast(this.$t('errors.errorLoading'));
            this.isLoading = false;
          });
      } else {
        this.isLoading = false;
      }
    },
    onSubmit() {
      this.$v.formObject.$touch();
      if (!this.$v.formObject.$invalid) {
        this.isLoading = true;
        this.create();
      }
    },
    addEmail() {
      this.$bvModal.show('value-change-modal');
    },
    emailAdded(email) {
      if (this.emailList.indexOf(email) < 0) {
        this.emailList.push(email);
      }
    },
    deleteEmail(email) {
      this.emailList = this.emailList.filter((x) => x !== email);
    },
    create() {
      let apiWrapper = {
        idPublic: null,
        name: this.formObject.name,
        contact: this.formObject.contact,
        phone: this.formObject.phone,
        email: this.emailList.join(';'),
        link: this.formObject.link,
        method: this.formObject.method,
        language: this.formObject.language,
        address: this.formObject.address,
        paymentMethod: this.formObject.paymentMethod,
        paymentCondition: this.formObject.paymentCondition,
      };

      if (this.$route.name == 'editSupplier') {
        apiWrapper.idPublic = this.$route.params.idPublic;
        SuppliersRepository.update(apiWrapper, this.$route.params.idPublic)
          .then(() => {
            this.$router.push({ name: 'suppliersConfig' });
          })
          .catch(() => {
            this.$toast.errorToast(this.$t('errors.errorLoading'));
            this.isLoading = false;
          });
      } else {
        SuppliersRepository.create(apiWrapper)
          .then(() => {
            this.$router.push({ name: 'suppliersConfig' });
          })
          .catch(() => {
            this.$toast.errorToast(this.$t('errors.errorLoading'));
            this.isLoading = false;
          });
      }
    },
    cancel() {
      this.$router.push({ name: 'suppliersConfig' });
    },
  },
};
</script>

<template>
  <HeaderContentLayout
    @ctrl-enter-pressed="onSubmit()"
    :title="
      $route.name == 'editSupplier' ? $t('general.edit') : $t('general.create')
    "
  >
    <template v-slot:header>
      <b-button
        type="submit"
        class="mr-2"
        variant="success"
        @click="onSubmit"
        >{{
          $route.name == 'editSupplier'
            ? $t('general.save')
            : $t('general.create')
        }}</b-button
      >

      <b-button
        @click="cancel"
        tabindex="-1"
        type="button"
        variant="secondary"
        >{{ $t('general.cancel') }}</b-button
      >
    </template>

    <loading
      :active.sync="isLoading"
      :can-cancel="false"
      color="#f1b44c"
      :width="70"
      :height="70"
      loader="dots"
      :is-full-page="false"
    >
    </loading>
    <b-form>
      <b-form-group
        id="input-group-name"
        :label="$t('general.name')"
        label-for="input-name"
      >
        <b-form-input
          id="input-name"
          :class="{
            'is-invalid': $v.formObject.name.$error,
          }"
          v-model="formObject.name"
          type="text"
          :placeholder="$t('general.name')"
        ></b-form-input>
        <Tooltip :validations="$v.formObject.name" />
      </b-form-group>

      <b-form-group
        id="input-group-contact"
        :label="$t('suppliers.contact')"
        label-for="input-contact"
      >
        <b-form-input
          id="input-contact"
          v-model="formObject.contact"
          type="text"
          :placeholder="$t('suppliers.contact')"
        ></b-form-input>
      </b-form-group>

      <b-form-group
        id="input-group-phone"
        :label="$t('suppliers.phone')"
        label-for="input-phone"
      >
        <b-form-input
          id="input-phone"
          v-model="formObject.phone"
          type="tel"
          :placeholder="$t('suppliers.phone')"
        ></b-form-input>
      </b-form-group>

      <b-form-group
        id="input-group-link"
        :label="$t('general.link')"
        label-for="input-link"
      >
        <b-form-input
          id="input-link"
          v-model="formObject.link"
          type="tel"
          :placeholder="$t('general.link')"
        ></b-form-input>
      </b-form-group>

      <b-form-group
        id="input-group-method"
        :label="$t('suppliers.communicationMethod')"
        label-for="input-method"
      >
        <b-form-select id="input-method" v-model="formObject.method">
          <b-form-select-option value="phone">{{
            $t('suppliers.phoneLink')
          }}</b-form-select-option>
          <b-form-select-option value="pdf">{{
            $t('suppliers.emailPDF')
          }}</b-form-select-option>
          <b-form-select-option value="link">{{
            $t('suppliers.emailLink')
          }}</b-form-select-option>
        </b-form-select>
      </b-form-group>

      <b-form-group
        id="input-group-language"
        :label="$t('suppliers.communicationLanguage')"
        label-for="input-language"
      >
        <b-form-select id="input-language" v-model="formObject.language">
          <b-form-select-option value="en">{{
            $t('general.english')
          }}</b-form-select-option>
          <b-form-select-option value="fr">{{
            $t('general.french')
          }}</b-form-select-option>
        </b-form-select>
      </b-form-group>

      <b-form-group
        id="input-group-payment-method"
        :label="$t('suppliers.paymentMethod')"
        label-for="input-payment-method"
      >
        <b-form-input
          id="input-payment-method"
          v-model="formObject.paymentMethod"
          type="text"
          :placeholder="$t('suppliers.paymentMethod')"
        ></b-form-input>
      </b-form-group>

      <b-form-group
        id="input-group-payment-condition"
        :label="$t('suppliers.paymentCondition')"
        label-for="input-payment-condition"
      >
        <b-form-input
          id="input-payment-condition"
          v-model="formObject.paymentCondition"
          type="text"
          :placeholder="$t('suppliers.paymentCondition')"
        ></b-form-input>
      </b-form-group>

      <b-form-group
        id="input-group-address-group"
        :label="$t('general.address')"
        label-for="input-address"
      >
        <b-form-textarea
          id="input-address"
          rows="7"
          v-model="formObject.address"
        ></b-form-textarea>
      </b-form-group>

      <b-form-group id="input-group-email" label-for="input-language">
        <template v-slot:label>
          <div class="d-flex justify-content-between align-items-end">
            <div>{{ $t('general.emails') }}</div>
            <b-button
              @click="addEmail"
              class="text-right"
              size="sm"
              variant="success"
              >{{ $t('general.add') }}</b-button
            >
          </div>
        </template>
        <ul>
          <li v-for="(item, index) in emailList" :key="index">
            <div
              class="d-flex justify-content-between align-items-end"
              style="font-size: 1.1em"
            >
              <div>{{ item }}</div>
              <i
                class="fas fa-trash text-danger"
                @click="deleteEmail(item)"
                style="cursor: pointer"
              ></i>
            </div>
          </li>
        </ul>
      </b-form-group>
    </b-form>

    <ChangeValueModal
      @save="emailAdded"
      :title="$t('general.email')"
      :button-text="$t('general.add')"
    />
  </HeaderContentLayout>
</template>
